import {
  DEFINITION_FILTER_KEYS,
  type AudienceDefinition,
  type SavedAudienceFilters,
} from './audience-definition';

/**
 * Every part a saved audience can set, listed once (NPJB-915).
 *
 * The Save check and the filter chip used to list the parts by hand, and each new part
 * (Target Lists in NPJB-701, technology in NPJB-764) reached the Save check but not the chip,
 * which then read "No filters" on a narrowed audience. Both now read the parts from here, and
 * the types make a new part hard to miss: a new definition field fails `READERS` until it is
 * read, and a new part key fails `PART_SHOWN_IN` until the page has a place to show it.
 */

export type AudiencePartKey =
  | keyof SavedAudienceFilters
  | 'targetLists'
  | 'signalTopics'
  | 'signalSources'
  | 'signalStages'
  | 'technologyUses'
  | 'technologyNotUses';

/** One part the audience sets, with what it picked — never empty. */
export interface AudiencePart {
  key: AudiencePartKey;
  /** Option values, topic or technology ids, or Target List upload ids. */
  ids: readonly string[];
}

/** Where the audience page tells the seller a part is set. `nowhere` is a gap, not a design. */
export type PartShownIn = 'filterChip' | 'sourcesChip' | 'nowhere';

export const PART_SHOWN_IN: Record<AudiencePartKey, PartShownIn> = {
  industry: 'filterChip',
  subIndustry: 'filterChip',
  employeeRange: 'filterChip',
  revenueRange: 'filterChip',
  seniority: 'filterChip',
  jobFunction: 'filterChip',
  geoRegion: 'filterChip',
  country: 'filterChip',
  technologyUses: 'filterChip',
  technologyNotUses: 'filterChip',
  targetLists: 'filterChip',
  signalTopics: 'filterChip',
  // The sources chip beside the filter chip names them and edits them in place.
  signalSources: 'sourcesChip',
  // Carried and always empty: nothing in the app can set a stage.
  signalStages: 'nowhere',
};

/** Not parts: `version` is ours, and the AI prompt is how the parts were picked, not one of them. */
type PartField = Exclude<keyof AudienceDefinition, 'version' | 'aiSearchPrompt'>;

/** Each named list in `field` as a part. `keys` must name every list, so a new one fails here. */
const partsOf = <T extends { [K in keyof T]: readonly string[] }>(
  field: T,
  keys: Record<keyof T, AudiencePartKey>,
): AudiencePart[] =>
  (Object.keys(keys) as (keyof T)[]).map((name) => ({ key: keys[name], ids: field[name] }));

/** One reader per definition field, so a new field fails here until it is read. */
const READERS: Record<PartField, (definition: AudienceDefinition) => AudiencePart[]> = {
  filters: ({ filters }) =>
    DEFINITION_FILTER_KEYS.map((key) => ({ key, ids: filters[key].map(({ value }) => value) })),
  signals: ({ signals }) =>
    partsOf(signals, { topics: 'signalTopics', source: 'signalSources', stage: 'signalStages' }),
  technology: ({ technology }) =>
    partsOf(technology, { uses: 'technologyUses', notUses: 'technologyNotUses' }),
  targetLists: ({ targetLists }) => [
    { key: 'targetLists', ids: targetLists.map(({ uploadId }) => uploadId) },
  ],
};

/** The parts this audience sets, in definition order: filters, signals, technology, lists. */
export const audienceParts = (definition: AudienceDefinition): AudiencePart[] =>
  Object.values(READERS)
    .flatMap((read) => read(definition))
    .filter(({ ids }) => ids.length > 0);

/**
 * True when nothing at all is selected (NPJB-711 AC7, NPJB-722 AC10).
 *
 * Every part counts, wherever the page shows it: an audience holding only signal topics is a
 * real audience, and so is one holding only a country. Mirrors `AudienceDefinition.is_empty()`
 * on the backend, which refuses the same request — this copy refuses it without a round trip.
 */
export const isDefinitionEmpty = (definition: AudienceDefinition): boolean =>
  audienceParts(definition).length === 0;
